// Centralized copy + media so brand strings stay sanitized.

export const BRAND = {
 name: "TeleMedCare",
 tagline: "Better health, one message away",
 logoUrl: "https://pub-7625fe7066bd4032bafed4ca91bd567f.r2.dev/logo/6cffd5de-3308-48f0-8607-40b496f51c41.png",
};

/** Patient-facing entry points shown in CTAs */
export const CONTACT_LINKS = {
 whatsapp: {
 // wa.me drops the leading + automatically.
 href: "https://wa.me/2348105444731",
 label: "+234 810 544 4731",
 },
};

/** Portal pages - the actual product behind the landing page. */
export const PORTAL_LINKS = {
 patientSignup: "https://portal.telemedcare.app/patient/signup",
 patientLogin: "https://portal.telemedcare.app/patient/login",
 doctorApply: "https://portal.telemedcare.app/apply/doctor",
 partnerApply: "https://portal.telemedcare.app/apply/partner",
};

export const NAV = [
 {
 label: "Care",
 children: [
 { label: "24/7 Doctor Visits", href: "#care-247" },
 { label: "Primary Care", href: "#primary-care" },
 { label: "Mental Health", href: "#mental-health" },
 { label: "Chronic Conditions", href: "#chronic-care" },
 ],
 },
 {
 label: "AI",
 children: [
 { label: "AI Symptom Check", href: "#ai-symptom" },
 { label: "Care Companion", href: "#whatsapp-care" },
 ],
 },
 { label: "For Doctors", href: "/for-doctors" },
 { label: "Hospitals & Clinics", href: "/for-hospitals" },
];

export const SERVICES = [
 {
 id: "care-247",
 eyebrow: "24/7 care",
 title: "Talk to a doctor, day or night",
 body:
 "From cough, malaria-like symptoms and infections to allergies and rashes get a licensed Nigerian doctor in minutes, on the channel you already use every day.",
 bullets: ["WhatsApp, voice and video", "Pidgin, Yoruba, Igbo and Hausa supported", "Prescriptions delivered to your door"],
 href: "https://wa.me/2348105444731",
 image: "/lady-texting.png",
 image_alt: "A Nigerian woman chatting with her TeleMedCare doctor on WhatsApp",
 align: "right",
 },
 {
 id: "primary-care",
 eyebrow: "Primary care",
 title: "A regular doctor for your whole family",
 body:
 "Pick a primary clinician who knows your story. Routine check-ups, preventive care, vaccinations and prescriptions all in one continuous record.",
 bullets: ["Same clinician every visit", "Family plans for up to 6", "Lab requests with home pickup"],
 href: "https://wa.me/2348105444731",
 image: "/family-picture.png",
 image_alt: "A Nigerian family at home together, all covered under one TeleMedCare family plan",
 align: "left",
 },
 {
 id: "mental-health",
 eyebrow: "Mental health",
 title: "Therapy that meets you where you are",
 body:
 "Speak with vetted therapists and psychiatrists for anxiety, burnout, grief and more. Sessions over secure chat, voice or video no waiting room, no stigma.",
 bullets: ["Match in under 24 hours", "Session notes you actually own", "Confidential, encrypted by default"],
 href: "https://wa.me/2348105444731",
 image: "https://images.unsplash.com/photo-1758874384553-b99bae0db3c8",
 image_alt: "A young woman smiling while reading her phone in soft daylight",
 align: "right",
 },
 {
 id: "chronic-care",
 eyebrow: "Chronic conditions",
 title: "Stay on top of diabetes, BP and asthma",
 body:
 "Personal coaches, connected devices and gentle WhatsApp nudges keep you on plan. We share the data with your doctor so every visit starts where the last one left off.",
 bullets: ["Connected glucose & BP monitors", "Refill reminders that actually arrive", "Coach-led 12-week programs"],
 href: "https://wa.me/2348105444731",
 image: "https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg",
 image_alt: "A Black doctor reviewing a patient's chronic care plan",
 align: "left",
 },
 {
 id: "ai-symptom",
 eyebrow: "AI symptom check",
 title: "Care guidance, before you book",
 body:
 "Our AI Care Companion supervised by clinicians and tuned to local conditions helps you describe symptoms, prepare for your visit and triage urgency in seconds.",
 bullets: ["Trained on local presentation patterns", "Clinician review on every flagged case", "Seamless handoff to a human"],
 href: "https://wa.me/2348105444731",
 image: "https://images.pexels.com/photos/3938023/pexels-photo-3938023.jpeg",
 image_alt: "A patient typing into a chat interface on their phone",
 align: "right",
 },
 {
 id: "whatsapp-care",
 eyebrow: "WhatsApp first",
 title: "Care that lives in the chat you already use",
 body:
 "No new app to download. Send a message, share a photo, get a follow-up. The whole TeleMedCare experience booking, prescriptions, refills, results fits inside WhatsApp.",
 bullets: ["Book and pay inside the chat", "Photos, voice notes and PDFs supported", "Reminders timed to your schedule"],
 href: "https://wa.me/2348105444731",
 image: "https://images.pexels.com/photos/4058316/pexels-photo-4058316.jpeg",
 image_alt: "A young Nigerian woman messaging her care team",
 align: "left",
 },
];

export const STATS = [
 { value: "2.4M+", label: "Conversations handled across WhatsApp, voice and video" },
 { value: "98%", label: "Members say they were heard and understood" },
 { value: "11min", label: "Average time from message to a licensed doctor" },
];

export const AUDIENCES = [
 {
 id: "patients",
 label: "For patients",
 title: "Care designed around your life not the clinic schedule",
 body:
 "Whether you’re in Lagos, Abuja or a rural community, TeleMedCare meets you where you are. Send a message, get matched, see a doctor.",
 points: [
 "Affordable plans starting from a few thousand naira a month",
 "Family coverage for up to six dependents",
 "Care notes and prescriptions saved for every visit",
 ],
 cta: { label: "Get care now", href: "https://wa.me/2348105444731", external: true },
 image: "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg",
 image_alt: "A patient comfortably consulting from home on their phone",
 },
 {
 id: "doctors",
 label: "For doctors",
 title: "Practice anywhere. Spend your time on care, not paperwork.",
 body:
 "Pick the hours that work for you and join a vetted network. We handle scheduling, encrypted records, billing and patient follow-up so you can focus on clinical work.",
 points: [
 "Flexible shifts from your laptop or phone",
 "AI scribe drafts every visit note for your review",
 "Transparent payouts paid weekly",
 ],
 cta: { label: "Join the network", href: "/for-doctors" },
 image: "/doctor-black.png",
 image_alt: "A Nigerian TeleMedCare doctor in a branded white coat at his desk",
 imageAspect: "5/4",
 imageFit: "contain",
 },
 {
 id: "hospitals",
 label: "Hospitals & clinics",
 title: "Extend your reach without expanding your floor space",
 body:
 "Stand up a virtual care line in weeks. TeleMedCare integrates with your existing EMR, ushers triaged patients to your specialists and surfaces analytics your board will actually use.",
 points: [
 "EMR-friendly handoff and dual-write",
 "Branded WhatsApp + web for your patients",
 "Clinical analytics, audit logs and HIPAA-aligned controls",
 ],
 cta: { label: "Apply as a partner", href: "/for-hospitals" },
 image: "/clinic-partnering.png",
 image_alt: "A TeleMedCare partner team - hospital, clinic and pharmacy staff",
 imageAspect: "3/2",
 imageFit: "contain",
 },
];

export const TESTIMONIALS = [
 {
 name: "Adaeze O.",
 role: "TeleMedCare member, Lagos",
 quote:
 "I sent one message at 11pm and a doctor was reviewing my son’s rash within ten minutes. The prescription arrived at our gate the next morning.",
 },
 {
 name: "Dr. Tunde A.",
 role: "Family physician, Ibadan",
 quote:
 "I see patients between hospital shifts now. The AI scribe writes a clean note and the platform handles the admin I just listen to the patient.",
 },
 {
 name: "Chiamaka E.",
 role: "Mother of two, Enugu",
 quote:
 "It feels like having a family doctor on speed dial. The reminders for my daughter’s asthma plan are quiet, kind and impossible to ignore.",
 },
 {
 name: "Ngozi K.",
 role: "Operations Lead, partner clinic",
 quote:
 "Within six weeks our virtual line was triaging non-urgent visits. Our specialists finally have time for the cases only they can handle.",
 },
];

export const PARTNER_LOGOS = [
 "MeridianCare Group",
 "Kano Health Trust",
 "Sahel Medical",
 "Lagos Federal Network",
 "AfricaMedTech",
 "Niger Wellness",
 "Pearl Insurance",
 "Northstar Plans",
 "Gulf of Guinea Hospitals",
 "OneClinic Africa",
];

/**
 * Provider categories that can onboard onto TeleMedCare. Mirrors the PartnerType enum
 * in the portal app — keep these in sync so doctors can route to every category that
 * partners can register for.
 *
 * `icon` is a lucide-react icon component name (resolved at render time).
 */
export const PARTNER_CATEGORIES = [
 {
 id: "pharmacy",
 label: "Pharmacies",
 icon: "Pill",
 blurb: "Dispense prescriptions and deliver medications to patients' doors.",
 },
 {
 id: "lab",
 label: "Laboratories",
 icon: "FlaskConical",
 blurb: "Run blood work, malaria panels and routine tests with same-day results.",
 },
 {
 id: "hospital",
 label: "Hospitals & clinics",
 icon: "Hospital",
 blurb: "Receive in-person and walk-in referrals from our doctor network.",
 },
 {
 id: "specialist",
 label: "Specialist practitioners",
 icon: "Stethoscope",
 blurb: "Cardiologists, dermatologists, paediatricians and other consultants.",
 },
 {
 id: "home-care",
 label: "Home care & nursing",
 icon: "HeartPulse",
 blurb: "Home nursing, post-op recovery and caregiver visits.",
 },
 {
 id: "physiotherapy",
 label: "Physiotherapy",
 icon: "Dumbbell",
 blurb: "Sports rehab, post-stroke recovery and home physio sessions.",
 },
 {
 id: "dental",
 label: "Dental",
 icon: "Smile",
 blurb: "Dental clinics for cleaning, restorative work and orthodontics.",
 },
 {
 id: "optical",
 label: "Optical & eye care",
 icon: "Eye",
 blurb: "Eye exams, prescription glasses and glaucoma screening.",
 },
 {
 id: "mental-health",
 label: "Mental health",
 icon: "Brain",
 blurb: "Psychologists, psychiatrists and licensed counsellors.",
 },
 {
 id: "nutrition",
 label: "Nutrition & dietetics",
 icon: "Apple",
 blurb: "Dietitians for diabetes plans, weight management and antenatal care.",
 },
 {
 id: "radiology",
 label: "Imaging & radiology",
 icon: "ScanLine",
 blurb: "X-ray, ultrasound, CT and MRI centres with digital reporting.",
 },
 {
 id: "ambulance",
 label: "Ambulance & transport",
 icon: "Ambulance",
 blurb: "Emergency response and inter-facility patient transport.",
 },
];

/** Dedicated “join the network” page for clinicians */
export const DOCTOR_NETWORK = {
 hero: {
 eyebrow: "For physicians & advanced practice providers",
 title: "Join the TeleMedCare clinical network",
 body:
 "See patients on your schedule, with AI-assisted documentation, secure messaging, and operations handled for you. We’re building a vetted network of licensed clinicians who want less admin and more time at the bedside, virtual or in person.",
 image: "/doctor-black.png",
 image_alt: "A Nigerian TeleMedCare doctor smiling at his desk in a branded white coat",
 },
 stats: [
 { value: "2-4 wks", label: "Typical credentialing timeline once documents are complete" },
 { value: "Weekly", label: "Transparent payouts with clear per-visit and stipend models" },
 { value: "24/7", label: "Platform, IT, and clinical-ops support for active shifts" },
 { value: "100%", label: "Encrypted charting, audit trails, and role-based access" },
 ],
 requirements: {
 title: "What we need from you",
 subtitle:
 "Requirements help us keep members safe, meet regulatory expectations, and set you up for a smooth first shift.",
 items: [
 {
 title: "Active license & good standing",
 detail:
 "Unrestricted license to practice in the jurisdiction(s) where your patients are located (or explicit telehealth registration where required). No current suspension or voluntary surrender.",
 },
 {
 title: "Professional indemnity / malpractice",
 detail:
 "Occurrence or claims-made policy meeting our network minimums, with TeleMedCare listed as additional insured or certificate holder as applicable.",
 },
 {
 title: "Identity & background screening",
 detail:
 "Government-issued ID, medical council / board verification, NPDB or equivalent where available, and sanctions checks (OIG, state boards).",
 },
 {
 title: "Prescribing & controlled substances (if applicable)",
 detail:
 "Valid DEA or local controlled-substance authority where your scope includes e-prescribing; adherence to tele-prescribing laws and platform protocols.",
 },
 {
 title: "Clinical workspace & connectivity",
 detail:
 "Private, quiet setting; HD webcam; headset; backup internet; and a commitment to our response-time SLAs during booked shifts.",
 },
 {
 title: "Telehealth competency",
 detail:
 "Completion of our orientation (documentation standards, escalation paths, cultural and language resources, and AI co-pilot usage).",
 },
 ],
 },
 process: {
 title: "How joining works",
 steps: [
 { step: "1", title: "Apply", body: "Submit the short clinical application and upload core documents (license, CV, malpractice face sheet)." },
 { step: "2", title: "Credentialing", body: "Our team verifies primary sources, references, and payor enrollment where relevant." },
 { step: "3", title: "Platform training", body: "Live or async modules on charting, prescribing workflows, and member experience." },
 { step: "4", title: "First shifts", body: "Shadow optional, then scheduled blocks with real-time clinical and technical backup." },
 ],
 },
 applyCta: {
 label: "Start application",
 href: "https://portal.telemedcare.app/apply/doctor",
 helper: "Takes about 10 minutes. You can save progress and return later.",
 },
};

/**
 * Hantavirus awareness page content. Awareness-only - we are not diagnosing
 * online; the destination is always WhatsApp where a licensed doctor responds.
 */
export const HANTAVIRUS = {
 hero: {
 eyebrow: "Public health awareness",
 title: "Hantavirus: know the signs. Talk to a doctor today.",
 body:
 "Hantavirus is a rare but serious illness spread by infected rodents. Catching it early saves lives. If you’ve had rodent exposure or are noticing fever, body aches, headaches and breathing trouble, message a TeleMedCare doctor on WhatsApp — we’ll triage you in minutes.",
 image: "/hantavirus.png",
 image_alt:
 "Illustration of a Hantavirus virion with electron microscope inset and a deer mouse - the natural reservoir of Hantavirus",
 },
 stats: [
 { value: "1-8 wks", label: "Typical incubation from exposure to first symptoms" },
 { value: "~38%", label: "Reported fatality of Hantavirus Pulmonary Syndrome when care is delayed" },
 { value: "Minutes", label: "From WhatsApp message to a licensed Nigerian doctor reviewing your case" },
 { value: "24/7", label: "Always-on triage - day, night, weekends and public holidays" },
 ],
 about: {
 eyebrow: "What is it?",
 title: "A rodent-borne virus you should never ignore",
 body:
 "Hantaviruses (family Hantaviridae, genus Orthohantavirus) are carried by certain rodents - most often the deer mouse, but also rats and other field mice. People become infected when they breathe in tiny airborne particles from rodent urine, droppings or saliva - usually while sweeping a dusty shed, store room, farm building or seldom-used room.",
 highlights: [
 "Not spread person-to-person for most strains common in West Africa and the Americas.",
 "Risk rises in homes, farms, shops or warehouses with active rodent infestation.",
 "Early symptoms look like malaria or flu - which is why a clinician should review them.",
 ],
 },
 symptoms: {
 title: "Symptoms to watch for",
 subtitle:
 "Symptoms usually start 1 to 8 weeks after exposure. They progress in two phases. If any severe symptom appears, do not wait - message a doctor immediately.",
 early: {
 label: "Early phase (first 1-5 days)",
 tone: "warning",
 items: [
 "Fever and chills",
 "Severe muscle aches (thighs, hips, back, shoulders)",
 "Headache",
 "Fatigue / unusual tiredness",
 "Dizziness",
 "Nausea, vomiting, diarrhoea or abdominal pain",
 ],
 },
 severe: {
 label: "Severe phase (4-10 days in)",
 tone: "danger",
 items: [
 "Shortness of breath / difficulty breathing",
 "Coughing - sometimes with fluid",
 "Tightness or heaviness in the chest",
 "Rapid heartbeat",
 "Low blood pressure or fainting",
 "Reduced urine output (kidney involvement)",
 ],
 },
 },
 transmission: {
 title: "How Hantavirus spreads",
 subtitle:
 "Knowing how exposure happens is the fastest way to protect your family.",
 routes: [
 {
 title: "Breathing contaminated dust",
 body: "Sweeping or vacuuming areas with rodent urine, droppings or nesting material can aerosolise the virus.",
 },
 {
 title: "Direct contact",
 body: "Touching surfaces, food or water contaminated by rodents and then touching your eyes, nose or mouth.",
 },
 {
 title: "Bites and scratches",
 body: "Less common, but a bite or scratch from an infected rodent can transmit the virus.",
 },
 {
 title: "Contaminated food",
 body: "Eating food that has been chewed, urinated or defecated on by infected rodents.",
 },
 ],
 },
 prevention: {
 title: "How to protect yourself and your family",
 items: [
 {
 title: "Seal up the house",
 detail: "Close holes and gaps around doors, windows, vents, water pipes and the roof. Rodents only need a 6mm gap to squeeze in.",
 },
 {
 title: "Trap and remove rodents safely",
 detail: "Use snap or bait traps. Always wear gloves to dispose of carcasses and double-bag them before throwing in a closed bin.",
 },
 {
 title: "Don't sweep dry droppings",
 detail: "Spray the area with a bleach solution (1 part bleach to 10 parts water), wait 5 minutes, then wipe with a damp cloth or paper towels. Never vacuum or dry-sweep.",
 },
 {
 title: "Store food properly",
 detail: "Keep grains, rice, garri and leftovers in tight metal or thick plastic containers. Clean up spills immediately and don't leave dishes overnight.",
 },
 {
 title: "Ventilate before you clean",
 detail: "Open windows and doors of a shed, store room or seldom-used room for 30 minutes before going in to clean.",
 },
 {
 title: "Wear PPE for clean-ups",
 detail: "If you must clean a heavily infested area, wear gloves, an N95 mask and goggles. Wash hands and clothes thoroughly afterwards.",
 },
 ],
 },
 redFlags: {
 eyebrow: "When to talk to a doctor now",
 title: "Don't wait. A 30-second message can save a life.",
 body:
 "If you've had recent rodent exposure - or anyone in your household is showing flu-like symptoms along with shortness of breath, chest tightness or persistent vomiting - message us on WhatsApp immediately. A licensed Nigerian doctor will triage your case and tell you whether to head to the nearest hospital.",
 bullets: [
 "Trouble breathing or fast, shallow breathing",
 "Chest pain or feeling of suffocation",
 "Fever with severe body aches that won't ease",
 "Confusion, dizziness or fainting",
 "Recent cleaning of rodent-infested areas",
 ],
 },
 faqs: [
 {
 q: "Is Hantavirus contagious between people?",
 a: "For the strains most commonly seen in Africa and the Americas, no - person-to-person spread is not typical. A specific South American strain (Andes virus) is the rare exception. The usual route is breathing in dust contaminated by rodent waste.",
 },
 {
 q: "Can it be treated?",
 a: "There is no specific antiviral cure, which is why early supportive care matters so much. Patients caught early do dramatically better - oxygen support, fluids and ICU-level care when needed can be life-saving.",
 },
 {
 q: "How quickly should I message a doctor?",
 a: "If you have flu-like symptoms AND any rodent exposure in the past 1-8 weeks, send a message today. If breathing feels off, message right now and head to a hospital while a doctor is reviewing your case.",
 },
 {
 q: "Does TeleMedCare diagnose Hantavirus over WhatsApp?",
 a: "We triage. Our doctors take your history, assess risk and either guide you through early care, refer you to a partner lab for testing, or send you to the nearest hospital - whichever your case needs.",
 },
 ],
 cta: {
 label: "Message a doctor on WhatsApp",
 href: "https://wa.me/2348105444731?text=Hello%20TeleMedCare%2C%20I%27d%20like%20to%20speak%20to%20a%20doctor%20about%20possible%20Hantavirus%20symptoms.",
 helper: "Available 24/7. No app to install. A licensed Nigerian doctor will review your case.",
 },
};

/** Hospital & clinic partnership page content */
export const HOSPITAL_PARTNERSHIP = {
 hero: {
 eyebrow: "Hospitals, health systems & clinics",
 title: "Partner with TeleMedCare",
 body:
 "Stand up a branded virtual front door in weeks: triage, routing, specialist access, and analytics your leadership team can use without ripping out your EMR.",
 image: "/clinic-partnering.png",
 image_alt: "A TeleMedCare partner team - doctor, nurse and pharmacist together",
 },
 valueProps: [
 {
 title: "Virtual demand without new bricks",
 body: "Absorb after-hours and low-acuity demand with a clinician network you don’t have to hire end-to-end.",
 },
 {
 title: "EMR-aware handoffs",
 body: "Bi-directional summaries, orders, and referrals aligned to your governanceno shadow IT.",
 },
 {
 title: "Governance you can defend",
 body: "Audit logs, break-glass rules, BAA-ready documentation, and privacy workflows your DPO can review.",
 },
 ],
 requirements: {
 title: "What we typically need from partners",
 subtitle:
 "Exact items depend on your country, payer mix, and architecturethese are the usual starting points for a security & clinical review.",
 items: [
 {
 title: "Licensed facility or accredited practice",
 detail: "Registration with the relevant ministry, council, or state health authority as a care delivery organization.",
 },
 {
 title: "Named clinical & administrative sponsors",
 detail: "A medical director (or equivalent) and an operations lead with authority to implement workflows and training.",
 },
 {
 title: "Privacy & security counterparts",
 detail: "DPO / compliance contact, IT security liaison, and approved subprocessors list for legal review.",
 },
 {
 title: "Clinical integration path",
 detail: "Preferred EMR or document exchange (FHIR, HL7, PDF export minimum), identity for clinicians, and escalation phone trees.",
 },
 {
 title: "Volume & coverage expectations",
 detail: "Target visit bands, languages, specialties, and hours of operation so we can staff and report accurately.",
 },
 {
 title: "Pilot success criteria",
 detail: "Shared KPIs: wait time, resolution rate, ED diversion, member NPS, and revenue cycle impact.",
 },
 ],
 },
 partnershipCta: {
 label: "Apply as a partner",
 href: "https://portal.telemedcare.app/apply/partner",
 helper: "Takes a few minutes - submit your details and our team will reach out within 1-2 business days.",
 },
};

export const FOOTER_NAV = [
 {
 title: "Care",
 links: [
 { label: "24/7 doctor visits", href: "/#care-247" },
 { label: "Primary care", href: "/#primary-care" },
 { label: "Mental health", href: "/#mental-health" },
 { label: "Chronic conditions", href: "/#chronic-care" },
 { label: "AI Care Companion", href: "/#whatsapp-care" },
 { label: "Hantavirus awareness", href: "/hantavirus" },
 ],
 },
 {
 title: "Get started",
 links: [
 { label: "Chat on WhatsApp", href: "https://wa.me/2348105444731", external: true },
 { label: "Patient sign up", href: "https://portal.telemedcare.app/patient/signup", external: true },
 { label: "Patient sign in", href: "https://portal.telemedcare.app/patient/login", external: true },
 ],
 },
 {
 title: "For partners",
 links: [
 { label: "Provider network", href: "/#network" },
 { label: "Doctors", href: "/for-doctors" },
 { label: "Hospitals & clinics", href: "/for-hospitals" },
 { label: "Apply as a doctor", href: "https://portal.telemedcare.app/apply/doctor", external: true },
 { label: "Apply as a partner", href: "https://portal.telemedcare.app/apply/partner", external: true },
 ],
 },
 {
 title: "Legal",
 links: [
 { label: "Privacy Policy", href: "/privacy" },
 { label: "Terms of Use", href: "/terms" },
 { label: "Data Deletion", href: "/delete-account" },
 ],
 },
];
